import {
  CoachMainFocus,
  CoachTimeframe,
  CoachFurtherEducationReason,
  CareerGoal,
  CareerChangeConcern,
  CareerTimeline,
  CareerAspect,
  SkipperType,
} from "../types/coach";
import { EnrollLikelihood } from "../types/formFunnel";
import { AccountCreationFunnel } from "../types/customerDetail";
import { EnrollmentStatus } from "../types/enrollmentItem";
import { ImageFileType } from "../types/imageFile";
import {
  NotificationApplicantNamespace,
  NotificationSchoolNamespace,
  NotificationStaffNamespace,
  NotificationTarget,
} from "../types/notification";

import {
  CombinedPaymentOptionConfig,
  employerSponsoredSchoolPaymentOptionDefaults,
  PaymentOptionMapType,
  schoolEmployerSponsoredOptionDefaults,
  schoolFinancialAidPaymentOptionDefaults,
  schoolNonPlatformPaymentOptionDefaults,
  schoolPayInFullPaymentOptionDefaults,
  SchoolPaymentOptionMapType,
  schoolPaymentPlanPaymentOptionDefaults,
  schoolPayPerLeadPaymentOptionDefaults,
  SponsorPaymentOptionMapType,
} from "../types/paymentOptions";
import {
  CohortDesignation,
  ProgramChannel,
  ProgramLevel,
  EligibilityRequirement,
  ProgramTag,
} from "../types/program";
import { DuplicateLeadBehaviorOption, ProviderType } from "../types/provider";
import { Role } from "../types/role";
import { VocationSubindustry } from "../types/vocationSubindustry";
import { RemoteType } from "../types/typesense/jobs/job";

export const providerTypeFriendlyNameMap: Record<ProviderType, string> = {
  SCHOOL: "School",
  SPONSOR: "Sponsor",
  EMPLOYER: "Employer",
} as const;
export const providerTypeQueryMap: Record<ProviderType, string> = {
  SCHOOL: "school",
  SPONSOR: "sponsor",
  EMPLOYER: "employer",
} as const;

export const duplicateLeadBehaviorMap: Record<DuplicateLeadBehaviorOption, string> = {
  SEND_ONCE_CHARGE_ONCE: "Send once charge once",
  SEND_ALL_CHARGE_ONCE: "Send all charge once",
  SEND_ALL_CHARGE_ALL: "Send all charge all",
};

export const enrollmentItemStatusMap: Record<EnrollmentStatus, string> = {
  PENDING: "Pending",
  LEAD: "Lead",
  CONTACT_MADE: "Contact Made",
  READY_FOR_REVIEW: "Applied",
  APPROVED: "Enrolled",
  ACTIVE: "Started",
  COMPLETE: "Completed",
  REJECTED: "Rejected",
  CANCELED: "Canceled",
  FAVORITE: "Favorite",
  CLOSED_LOST: "Closed Lost",
};

export const paymentOptionFriendlyNameMap: PaymentOptionMapType<string> = {
  EMPLOYER_SPONSORED_SCHOOL: "Employer sponsorship (sponsor)",
  SCHOOL_EMPLOYER_SPONSORED: "Employer sponsorship",
  SCHOOL_FINANCIAL_AID: "Financial aid",
  SCHOOL_NON_PLATFORM: "Non-marketplace (all payments happen off-platform)",
  SCHOOL_PAY_IN_FULL: "Pay in full",
  SCHOOL_PAY_PER_LEAD: "Pay per lead",
  SCHOOL_PAYMENT_PLAN: "Payment plan",
};

export const customerPaymentOptionFriendlyNameMap: PaymentOptionMapType<string> = {
  EMPLOYER_SPONSORED_SCHOOL: "Employer sponsorship (may be available)",
  SCHOOL_EMPLOYER_SPONSORED: "Employer sponsorship (may be available)",
  SCHOOL_FINANCIAL_AID: "Financial aid (may be available)",
  SCHOOL_NON_PLATFORM: "Pay school directly",
  SCHOOL_PAY_IN_FULL: "Pay in full",
  SCHOOL_PAY_PER_LEAD: "Pay nothing now",
  SCHOOL_PAYMENT_PLAN: "Payment plan",
};

export const schoolPaymentOptionFriendlyNameMap: SchoolPaymentOptionMapType<string> = {
  SCHOOL_EMPLOYER_SPONSORED: "Employer sponsorship",
  SCHOOL_FINANCIAL_AID: "Financial aid",
  SCHOOL_NON_PLATFORM: "Non-marketplace (all payments happen off-platform)",
  SCHOOL_PAY_IN_FULL: "Pay in full",
  SCHOOL_PAY_PER_LEAD: "Pay per lead",
  SCHOOL_PAYMENT_PLAN: "Payment plan",
};

export const sponsorPaymentOptionFriendlyNameMap: SponsorPaymentOptionMapType<string> = {
  EMPLOYER_SPONSORED_SCHOOL: "Employer sponsorship (sponsor)",
};

export const paymentOptionTypeToConfigDefaultsMap: PaymentOptionMapType<
  Partial<CombinedPaymentOptionConfig>
> = {
  EMPLOYER_SPONSORED_SCHOOL: employerSponsoredSchoolPaymentOptionDefaults,
  SCHOOL_EMPLOYER_SPONSORED: schoolEmployerSponsoredOptionDefaults,
  SCHOOL_FINANCIAL_AID: schoolFinancialAidPaymentOptionDefaults,
  SCHOOL_NON_PLATFORM: schoolNonPlatformPaymentOptionDefaults,
  SCHOOL_PAY_IN_FULL: schoolPayInFullPaymentOptionDefaults,
  SCHOOL_PAY_PER_LEAD: schoolPayPerLeadPaymentOptionDefaults,
  SCHOOL_PAYMENT_PLAN: schoolPaymentPlanPaymentOptionDefaults,
};

export const notificationTargetTypeEnumMap: Record<NotificationTarget, string> = {
  PROVIDER: "Provider",
  PROGRAM: "Program",
  CUSTOMER: "Customer",
  ENROLLMENT_ITEM: "Enrollment Item",
  PAYMENT_OPTION: "Payment option",
  PROGRAM_COST: "Program cost",
};

export const notificationSchoolStaffNamespaceEnumMap: Record<
  NotificationSchoolNamespace | NotificationStaffNamespace,
  string
> = {
  SCHOOL__ADDED_PAYMENT_OPTION: "added a payment option",
  SCHOOL__REMOVED_PAYMENT_OPTION: "removed a payment option",
  SCHOOL__UPDATED_PAYMENT_OPTION: "updated a payment option",
  SCHOOL__UPDATED_PROGRAM_MARKETPLACE_STATUS: "updated a program's marketplace status",
  SCHOOL__UPDATED_PROGRAM_COSTS: "updated a program's costs",
  SCHOOL__UPDATED_PARTNERED_STATUS: "updated their partnered status",
  SCHOOL__UPDATED_PROGRAM_COST: "updated a program cost",
  SCHOOL__REMOVED_PROGRAM_COST: "removed a program cost",
  SCHOOL__ADDED_PROGRAM_COST: "added a program cost",
  SCHOOL__DELETED_PROGRAM: "deleted a program",
  SCHOOL__APPROVED_ENROLLMENT_ITEM: "approved an enrollment item",
  SCHOOL__ONBOARDED_TO_STRIPE_CONNECT: "completed onboarding to Stripe Connect",
  STAFF__DELETED_PROGRAM: "deleted a program",
  SCHOOL__ENROLLMENT_STATUS__LEAD_SHOULD_CHARGE:
    "automatically moved an enrollment item to lead status, please charge them for this lead!",
};

export const notificationEnrollmentItemNamespaceEnumMap: Record<
  NotificationApplicantNamespace,
  string
> = {
  APPLICANT__ENROLLMENT_STATUS__READY_FOR_REVIEW: "is ready for review",
};

export const userRoleEnumMap: Record<Role, string> = {
  ADMIN: "Admin",
  CUSTOMER: "Customer",
  SCHOOL: "School",
  GENERAL: "General",
};

export const cohortDesignationEnumFriendlyMap: Record<CohortDesignation, string> = {
  REQUIRED: "Cohort-based (required)",
  ASYNC: "Self-paced",
  UNKNOWN: "Cohort-based",
};

export const imageFileTypeFriendlyMap: Record<ImageFileType, string> = {
  VOCATION_IMAGE: "Vocation image",
  PROGRAM_IMAGE: "Program image",
  PROVIDER_LOGO: "Provider logo",
  LANDING_TOP: "SEO Metro Page top image",
  LANDING_BOTTOM: "SEO Metro Page bottom image",
  LANDING_INLINE: "SEO Metro Page inline image",
  AFFILIATE_LOGO: "Affiliate logo",
  PROVIDER_TRUST_BADGE: "Trust badge",
};

export const programTagFriendlyMap: Record<ProgramTag, string> = {
  ACCREDITED: "Accredited",
  NOT_ACCREDITED: "Not accredited",
  SELF_PACED: "Self-paced",
  TOP_RATED: "Top rated",
  STUDENT_FAVORITE: "Student favorite",
  ACCELERATED: "Accelerated",
  FLEXIBLE_SCHEDULE: "Flexible schedule",
  BUDGET_FRIENDLY: "Budget friendly",
  QUICK_COMMUNICATION: "Quick communication",
  NONPROFIT: "Nonprofit",
  CAN_TRANSFER_CREDITS: "Can transfer credits",
  DEGREE_COMPLETION: "Degree completion",
  CHRISTIAN_UNIVERSITY: "Christian university",
};

export const programChannelFriendlyMap: Record<ProgramChannel, string> = {
  MARKETPLACE: "Marketplace",
  SOCIAL: "Social",
};

export const programLevelFriendlyMap: Record<ProgramLevel, string> = {
  CERTIFICATE: "Certificate/Diploma",
  ASSOCIATE: "Associate Degree",
  BACHELOR: "Bachelor's Degree",
  GRADUATE: "Graduate Degree",
};

export const eligibilityRequirementFriendlyMap: Record<EligibilityRequirement, string> = {
  NEEDS_HIGH_SCHOOL_DIPLOMA: "Needs high school diploma",
};

export const excludeEligibilityRequirementFriendlyMap: Record<EligibilityRequirement, string> = {
  NEEDS_HIGH_SCHOOL_DIPLOMA: "Does not require high school diploma",
};

export const VocationSubindustryFriendlyMap: Record<VocationSubindustry, string> = {
  BUSINESS_ADMINISTRATION: "Administration",
  BUSINESS_EDUCATION: "Education",
  BUSINESS_FINANCE_AND_ACCOUNTING: "Finance and accounting",
  BUSINESS_LEGAL: "Legal",
  BUSINESS_MARKETING_AND_DESIGN: "Marketing and design",
  HEALTH_ADMINISTRATIVE: "Administrative",
  HEALTH_CLINICAL_SUPPORT: "Clinical support",
  HEALTH_DIRECT_PATIENT_CARE: "Direct patient care",
  HEALTH_MENTAL_AND_PHYSICAL_WELLNESS: "Mental and physical wellness",
  TECHNOLOGY_CYBERSECURITY_AND_IT: "Cybersecurity and IT",
  TECHNOLOGY_ENGINEERING: "Engineering",
  TECHNOLOGY_PRODUCT_DEVELOPMENT: "Product development",
  TECHNOLOGY_SOFTWARE_AND_ANALYTICS: "Software and analytics",
  TRADE_COSMETOLOGY: "Cosmetology",
  TRADE_CULINARY: "Culinary",
  TRADE_TECHNICIAN: "Technician",
  TRADE_TRADES: "Trades",
  TRADE_TRANSPORTATION: "Transportation",
};

export const timeframeFriendlyMap: Record<CoachTimeframe, string> = {
  IMMEDIATELY: "Immediately",
  WITHIN_MONTH: "Within a month",
  ONE_TO_THREE_MONTHS: "1-3 months",
  THREE_TO_SIX_MONTHS: "3-6 months",
  SIX_TO_TWELVE_MONTHS: "6-12 months",
  OVER_A_YEAR: "Over a year",
};

export const mainFocusFriendlyMap: Record<CoachMainFocus, string> = {
  FAST_COMPLETION: "Fast completion",
  ONLINE: "Online",
  FLEXIBLE_SCHEDULE: "Flexible schedule",
  QUALITY: "Quality",
  LOW_COST: "Low cost",
  FOR_CREDIT: "For credit",
  EASE_OF_ENROLLING: "Ease of enrolling",
};

export const furtherEducationReasonFriendlyMap: Record<CoachFurtherEducationReason, string> = {
  CHANGE_CAREER: "To change careers or enter a new field",
  PROGRESS_CAREER: "To progress my career in my current industry",
  LEARN_SKILLS: "To learn something new and gain new skills",
  BETTER_ROLE_MODEL: "To become a better role model for my loved ones",
  INCREASE_EARNINGS: "To increase my earning potential",
  SKIP: "Skip",
};

export const accountCreationFunnelFriendlyMap: Record<AccountCreationFunnel, string> = {
  MODAL: "Create Account Modal",
  PROGRAM_PAGE: "Program Page",
  SKIPPER_COACH: "Skipper Coach",
  SKIPPER_FORM: "Skipper Form",
  SIGN_IN: "Sign In",
  FORM_FUNNEL: "Form Funnel",
};

export const careerGoalFriendlyMap: Record<CareerGoal, string> = {
  SWITCH_CAREERS: "Looking to switch careers",
  ADVANCE_IN_FIELD: "Advance in my field",
  GAIN_NEW_SKILLS: "Gain new skills or credentials",
  EXPLORE_OPTIONS: "Just exploring",
};

export const careerChangeConcernFriendlyMap: Record<CareerChangeConcern, string> = {
  EDUCATION_COST: "Cost of education",
  TIME_COMMITMENT: "Time commitment",
  CHANGE_RISK: "Risk of switching fields",
  MAY_NOT_LIKE_IT: "Not sure if I'll like it",
  FUTURE_OPPORTUNITIES: "Finding a job afterward",
};

export const careerTimelineFriendlyMap: Record<CareerTimeline, string> = {
  ZERO_TO_THREE_MONTHS: "Within 3 months",
  THREE_TO_SIX_MONTHS: "3 to 6 months",
  SIX_TO_TWELVE_MONTHS: "6 to 12 months",
  OVER_A_YEAR: "Over a year",
  JUST_EXPLORING: "Just exploring options",
};

export const careerAspectFriendlyMap: Record<CareerAspect, string> = {
  HIGHER_INCOME_POTENTIAL: "Higher income potential",
  WORK_LIFE_BALANCE: "Better work-life balance",
  MEANINGFUL_WORK: "More meaningful work",
  CAREER_GROWTH: "Career growth",
  JOB_SECURITY: "Job security",
};

export const skipperTypeFriendlyMap: Record<SkipperType, string> = {
  BUILDER: "Builder",
  MENTOR: "Mentor",
  CAPTAIN: "Captain",
  SCIENTIST: "Scientist",
  PLANNER: "Planner",
  HERO: "Hero",
  HOST: "Host",
  NAVIGATOR: "Navigator",
  CREATOR: "Creator",
  OPTIMIZER: "Optimizer",
  TEAMMATE: "Teammate",
  STAR: "Star",
  CONNECTOR: "Connector",
};

export const remoteTypeFriendlyMap: Record<RemoteType, string> = {
  HYBRID_REMOTE: "Hybrid",
  FULL_REMOTE: "Remote",
};

export const enrollLikelihoodFriendlyMap: Record<EnrollLikelihood, string> = {
  VERY_LIKELY: "Very likely",
  SOMEWHAT_LIKELY: "Somewhat likely",
  UNLIKELY: "Unlikely",
};
